import type { ReactNode } from "react"
import { ButtonLink } from "@/components/marketing/elements/button"
import { Plan, PricingMultiTier } from "@/components/marketing/sections/pricing-multi-tier"
import { ErrorState, LoadingState } from "@/components/QueryState"
import { getPublicLandingMockPlans, usePublicSubscriptionPlans } from "@/hooks/useSubscriptionPlans"
import { isLaunchMode, primaryCtaHref, primaryCtaLabel } from "@/lib/launch-config"
import {
  buildBetaPlanPriceDisplay,
  filterPublicLandingPlans,
  landingPlanDisplayName,
} from "@/lib/plan-marketing-copy"
import type { PublicSubscriptionPlan } from "@/lib/subscription-types"

function resolveLandingPlans(catalog: PublicSubscriptionPlan[] | undefined): PublicSubscriptionPlan[] {
  if (catalog) {
    const filtered = filterPublicLandingPlans(catalog)
    if (filtered.length > 0) return filtered
  }
  if (isLaunchMode()) return getPublicLandingMockPlans()
  return []
}

export function DynamicPricingSection({ footer }: { footer?: ReactNode }) {
  const plans = usePublicSubscriptionPlans()
  const landingPlans = resolveLandingPlans(plans.data)

  if (plans.isPending && landingPlans.length === 0) {
    return (
      <section id="pricing" className="py-16">
        <LoadingState />
      </section>
    )
  }

  if (plans.isError && landingPlans.length === 0) {
    return (
      <section id="pricing" className="py-16">
        <ErrorState error={plans.error} />
      </section>
    )
  }

  return (
    <>
      <PricingMultiTier
        id="pricing"
        headline="Pricing"
        plans={
          <>
            {landingPlans.map((plan) => {
              const priceDisplay = buildBetaPlanPriceDisplay(plan)
              const features = plan.marketingFeatures

              return (
                <Plan
                  key={plan.id}
                  name={landingPlanDisplayName(plan)}
                  price={priceDisplay.price}
                  period={priceDisplay.period}
                  note={priceDisplay.note}
                  subheadline={<p>{plan.description}</p>}
                  features={features}
                  cta={
                    <ButtonLink href={primaryCtaHref()} size="lg">
                      {primaryCtaLabel()}
                    </ButtonLink>
                  }
                />
              )
            })}
          </>
        }
      />
      {footer}
    </>
  )
}
